<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AppHeader from '@/components/AppHeader.vue';

const auth = useAuthStore();
const route = useRoute();

const fullBleed = computed(() => route.meta.fullBleed === true);

onMounted(async () => {
  if (auth.status === 'idle') await auth.fetchMe();
});
</script>

<template>
  <div class="app-shell" :class="{ 'app-shell--full-bleed': fullBleed }">
    <template v-if="!fullBleed">
      <div class="ambient-bg" aria-hidden="true">
        <div class="ambient-grid" />
        <div class="ambient-glow ambient-glow--violet" />
        <div class="ambient-glow ambient-glow--rose" />
      </div>

      <AppHeader />

      <main class="app-main">
        <RouterView v-slot="{ Component }">
          <Transition name="fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
      </main>

      <footer class="app-footer">
        <p>
          <span class="brand">watch.silxnt.dev</span>
          <span class="dot">·</span>
          <a href="https://about.silxnt.dev" target="_blank" rel="noopener noreferrer">about.silxnt.dev</a>
        </p>
      </footer>
    </template>

    <template v-else>
      <RouterView v-slot="{ Component }">
        <Transition name="fade" mode="out-in">
          <component :is="Component" />
        </Transition>
      </RouterView>
    </template>
  </div>
</template>

<style scoped>
.app-shell {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  isolation: isolate;
}

.app-shell--full-bleed {
  display: block;
}

.ambient-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}

.ambient-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(192, 132, 252, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(192, 132, 252, 0.04) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(ellipse at top, black 30%, transparent 75%);
}

.ambient-glow {
  position: absolute;
  width: 38rem;
  height: 38rem;
  border-radius: 50%;
  pointer-events: none;
  will-change: transform;
}

.ambient-glow--violet {
  background: radial-gradient(circle, rgba(192, 132, 252, 0.3) 0%, rgba(192, 132, 252, 0) 70%);
  top: -10rem;
  left: -8rem;
}

.ambient-glow--rose {
  background: radial-gradient(circle, rgba(244, 114, 182, 0.15) 0%, rgba(244, 114, 182, 0) 70%);
  bottom: -12rem;
  right: -8rem;
}

.app-main {
  flex: 1;
  width: min(1200px, 100% - 2rem);
  margin: 0 auto;
  padding: 1.5rem 0 4rem;
}

.app-footer {
  border-top: 1px solid var(--color-border-subtle);
  padding: 1.25rem 1rem;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.app-footer .brand {
  color: var(--color-accent-primary);
}

.app-footer .dot {
  margin: 0 0.75rem;
  opacity: 0.5;
}

.app-footer a {
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color 0.18s ease;
}

.app-footer a:hover {
  color: var(--color-text-primary);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
